// Keep in sync with EmailSignatureHtmlPolicy.cs - the server re-sanitizes with the same lists on save.
const ALLOWED_TAGS: ReadonlySet<string> = new Set([
    'a', 'b', 'blockquote', 'br', 'caption', 'center', 'col', 'colgroup', 'div', 'em', 'font', 'h1', 'h2',
    'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p', 's', 'small', 'span', 'strike', 'strong', 'sub',
    'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul'
]);

const DROP_WITH_CONTENT: ReadonlySet<string> = new Set([
    'audio', 'button', 'embed', 'form', 'head', 'iframe', 'input', 'link', 'math', 'meta', 'noscript',
    'object', 'script', 'style', 'svg', 'template', 'textarea', 'title', 'video'
]);

const GLOBAL_ATTRIBUTES: ReadonlySet<string> = new Set(['align', 'dir', 'style']);

// Outlook copies signatures as tables whose layout depends on border-collapse / border-spacing / box-sizing;
// dropping those lets the viewer's defaults re-flow the design.
const ALLOWED_STYLE_PROPERTIES: ReadonlySet<string> = new Set([
    'background-color', 'border', 'border-bottom', 'border-bottom-color', 'border-bottom-left-radius',
    'border-bottom-right-radius', 'border-bottom-style', 'border-bottom-width', 'border-collapse',
    'border-color', 'border-left', 'border-left-color', 'border-left-style', 'border-left-width',
    'border-radius', 'border-right', 'border-right-color', 'border-right-style', 'border-right-width',
    'border-spacing', 'border-style', 'border-top', 'border-top-color', 'border-top-left-radius',
    'border-top-right-radius', 'border-top-style', 'border-top-width', 'border-width', 'box-sizing', 'color',
    'display', 'font', 'font-family', 'font-size', 'font-style', 'font-weight', 'height', 'letter-spacing',
    'line-height', 'list-style-type', 'margin', 'margin-bottom', 'margin-left', 'margin-right', 'margin-top',
    'max-height', 'max-width', 'min-height', 'min-width', 'object-fit', 'object-position', 'overflow',
    'overflow-wrap', 'overflow-x', 'overflow-y', 'padding', 'padding-bottom', 'padding-left', 'padding-right',
    'padding-top', 'table-layout', 'text-align', 'text-decoration', 'text-indent', 'text-transform',
    'vertical-align', 'white-space', 'width', 'word-break', 'word-wrap'
]);

// Empty lines copied around the signature (Outlook's editor keeps them) - trimmed only at the outer edges.
const BLANK_LINE_TAGS: ReadonlySet<string> = new Set(['b', 'div', 'em', 'font', 'i', 'p', 'span', 'strong', 'u']);

const DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=\r\n]+$/i;
const MAX_DATA_IMAGE_CHARACTERS = 1400000;

export const TROVVE_SIGNATURE_URL = 'https://trovve.com/';

export function sanitizeEmailSignatureHtml(html: string): string {
    if (!html || !html.trim()) {
        return '';
    }

    const parser = new DOMParser();
    const documentNode = parser.parseFromString('<div data-vf-signature-root>' + html + '</div>', 'text/html');
    const root = documentNode.body.firstElementChild;
    if (!root) {
        return '';
    }

    sanitizeChildren(root);
    trimBlankEdges(root);
    return root.innerHTML.trim();
}

export function hasEmailSignatureContent(html: string): boolean {
    if (!html || !html.trim()) {
        return false;
    }

    const parser = new DOMParser();
    const documentNode = parser.parseFromString(html, 'text/html');
    if (documentNode.body.getElementsByTagName('img').length > 0) {
        return true;
    }

    return (documentNode.body.textContent || '').replace(/\u00a0/g, ' ').trim().length > 0;
}

export function shouldShowTrovveSignatureFallback(
    isForward: boolean,
    hasSavedSignature: boolean,
    currentSignatureHtml: string
): boolean {
    return !isForward && !hasSavedSignature && !hasEmailSignatureContent(currentSignatureHtml);
}

export function buildReplyHtmlWithSignature(bodyHtml: string, signatureHtml: string): string {
    const cleanSignature = sanitizeEmailSignatureHtml(signatureHtml || '');
    if (hasEmailSignatureContent(cleanSignature)) {
        return (bodyHtml || '')
            + '<div data-vf-email-signature="true" style="margin-top:24px">'
            + cleanSignature
            + '</div>';
    }

    return (bodyHtml || '')
        + '<div data-vf-email-signature="true" style="margin-top:24px">'
        + '<a href="' + TROVVE_SIGNATURE_URL + '" target="_blank" rel="noopener noreferrer">Sent from Trovve</a>'
        + '</div>';
}

function sanitizeChildren(parent: Element): void {
    Array.from(parent.children).forEach((element: Element) => sanitizeChild(element));
}

function sanitizeChild(element: Element): void {
    const tag = element.tagName.toLowerCase();
    const childElements = Array.from(element.children);

    if (DROP_WITH_CONTENT.has(tag)) {
        element.remove();
        return;
    }

    if (!ALLOWED_TAGS.has(tag)) {
        // Unwrapped children get the same tag checks, so a wrapper cannot smuggle in <style> or <script>.
        element.replaceWith(...Array.from(element.childNodes));
        childElements.forEach((child: Element) => sanitizeChild(child));
        return;
    }

    sanitizeElement(element);
}

function sanitizeElement(element: Element): void {
    const tag = element.tagName.toLowerCase();
    Array.from(element.attributes).forEach((attribute: Attr) => {
        const name = attribute.name.toLowerCase();
        if (!attributeIsAllowed(tag, name)) {
            element.removeAttribute(attribute.name);
            return;
        }

        if (name === 'style') {
            const cleanStyle = sanitizeStyle(attribute.value);
            if (cleanStyle) {
                element.setAttribute('style', cleanStyle);
            } else {
                element.removeAttribute('style');
            }
        }
    });

    if (tag === 'a') {
        const href = (element.getAttribute('href') || '').trim();
        if (!allowedUri(href, ['http://', 'https://', 'mailto:', 'tel:'])) {
            element.removeAttribute('href');
        } else {
            element.setAttribute('target', '_blank');
            element.setAttribute('rel', 'noopener noreferrer');
        }
    } else if (tag === 'img') {
        const source = (element.getAttribute('src') || '').trim();
        const validDataImage = source.length <= MAX_DATA_IMAGE_CHARACTERS && DATA_IMAGE.test(source);
        if (!validDataImage && !allowedUri(source, ['http://', 'https://'])) {
            element.remove();
            return;
        }
    }

    sanitizeChildren(element);
}

function trimBlankEdges(root: Element): void {
    while (root.firstChild && isBlankLine(root.firstChild)) {
        root.removeChild(root.firstChild);
    }
    while (root.lastChild && isBlankLine(root.lastChild)) {
        root.removeChild(root.lastChild);
    }
}

function isBlankLine(node: Node): boolean {
    const element = node as Element;
    const tag = element.tagName ? element.tagName.toLowerCase() : '';
    const style = element.getAttribute ? element.getAttribute('style') || '' : '';

    if (node.nodeType === Node.COMMENT_NODE) {
        return true;
    }
    if (node.nodeType === Node.TEXT_NODE) {
        return !hasVisibleText(node.textContent);
    }
    if (tag === 'br') {
        return true;
    }
    // A bordered or filled "empty" block is a drawn divider, not a blank line.
    if (!BLANK_LINE_TAGS.has(tag) || /border|background/i.test(style)) {
        return false;
    }
    return ['img', 'hr', 'table'].every((visualTag: string) => element.getElementsByTagName(visualTag).length === 0)
        && !hasVisibleText(element.textContent);
}

function hasVisibleText(text: string | null): boolean {
    return (text || '').replace(/ /g, ' ').trim().length > 0;
}

function attributeIsAllowed(tag: string, attribute: string): boolean {
    if (GLOBAL_ATTRIBUTES.has(attribute)) {
        return true;
    }
    if (tag === 'a') {
        return attribute === 'href' || attribute === 'target' || attribute === 'rel';
    }
    if (tag === 'img') {
        return ['alt', 'border', 'height', 'src', 'width'].indexOf(attribute) >= 0;
    }
    if (tag === 'table') {
        return ['bgcolor', 'border', 'cellpadding', 'cellspacing', 'height', 'width'].indexOf(attribute) >= 0;
    }
    if (tag === 'td' || tag === 'th') {
        return ['bgcolor', 'colspan', 'height', 'rowspan', 'valign', 'width'].indexOf(attribute) >= 0;
    }
    if (tag === 'tr') {
        return attribute === 'bgcolor' || attribute === 'height' || attribute === 'valign';
    }
    if (tag === 'col' || tag === 'colgroup') {
        return attribute === 'span' || attribute === 'valign' || attribute === 'width';
    }
    if (tag === 'font') {
        return attribute === 'color' || attribute === 'face' || attribute === 'size';
    }
    if (tag === 'hr') {
        return ['color', 'noshade', 'size', 'width'].indexOf(attribute) >= 0;
    }
    return false;
}

function sanitizeStyle(style: string): string {
    return (style || '')
        .split(';')
        .map((declaration: string) => declaration.trim())
        .filter((declaration: string) => {
            const separator = declaration.indexOf(':');
            if (separator <= 0) {
                return false;
            }
            const property = declaration.substring(0, separator).trim().toLowerCase();
            const value = declaration.substring(separator + 1).trim();
            return ALLOWED_STYLE_PROPERTIES.has(property)
                && !!value
                && value.length <= 500
                && !/expression\s*\(|javascript\s*:|url\s*\(|behavior\s*:|@import|-moz-binding/i.test(value);
        })
        .join(';');
}

function allowedUri(value: string, prefixes: string[]): boolean {
    const lower = value.toLowerCase();
    return !!value && prefixes.some((prefix: string) => lower.indexOf(prefix) === 0);
}
